import { ref, computed, readonly } from 'vue'
import { WebDirectChatbotApi } from '../api/web-direct'
import type { IWebDirectChatbotConfig } from '../api/web-direct/types'
import { AccessMode, ApiError } from '@sidd/chatbot-core'

export function useWebDirectChatbot() {
  const chatbot = ref<IWebDirectChatbotConfig | null>(null)
  const isLoading = ref(false)
  const error = ref<'not-found' | 'inactive' | 'error' | null>(null)

  /**
   * Extract slug from current hostname
   * Expected formats:
   * - Production: {slug}.ask.laivly.cloud (subdomain-based)
   * - Development: localhost:3001?slug={slug} (query param)
   */
  const slug = computed(() => {
    const hostname = window.location.hostname

    // Development: use query param
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return new URLSearchParams(window.location.search).get('slug')
    }

    // Production: extract from subdomain
    const domain = import.meta.env.VITE_WEBDIRECT_DOMAIN || 'ask.laivly.cloud'
    const pattern = new RegExp(`^([^.]+)\\.${domain.replace(/\./g, '\\.')}`)
    const match = hostname.match(pattern)
    return match?.[1] ?? null
  })

  /**
   * Whether this chatbot requires authentication
   */
  const requiresAuth = computed(
    () =>
      chatbot.value != null && chatbot.value.accessMode !== AccessMode._Public
  )

  async function loadChatbot(): Promise<boolean> {
    if (!slug.value) {
      error.value = 'not-found'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const config = await WebDirectChatbotApi.getBySlug(slug.value)
      chatbot.value = config
      if (!config.isActive) {
        error.value = 'inactive'
        return false
      }
      return true
    } catch (e: unknown) {
      chatbot.value = null
      // Check if 404
      if (e instanceof ApiError && e.isNotFound) {
        error.value = 'not-found'
      } else {
        error.value = 'error'
      }
      return false
    } finally {
      isLoading.value = false
    }
  }

  function retry(): void {
    loadChatbot()
  }

  return {
    chatbot: readonly(chatbot),
    slug: readonly(slug),
    isLoading: readonly(isLoading),
    error: readonly(error),
    requiresAuth,
    loadChatbot,
    retry,
  }
}
